import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  isDevMode,
  isSignal,
  effect,
  Signal
} from '@angular/core';
import { Column, Grid, ServerSideColumn } from '../types/grid';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  DataTypeDefinition,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowModelType
} from 'ag-grid-enterprise';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { isServerSideDataSource, isSSRMDataSource } from '../types/grid-data-source';
import { LanguageService } from '@softline/application';
import { Dictionary, distinct, equals, Filter, getValue, isDefinedNotEmpty, isResource } from '@softline/core';
import { formatter } from '@softline/ui-core';
import { ColGroupDef } from 'ag-grid-community/dist/types/src/entities/colDef';
import { toAgGridColDefs } from './to-ag-grid-col-defs.function';
import { GridLoadingOverlay } from './overlays/grid-loading.overlay';
import { GridNoRowsOverlay } from './overlays/grid-no-rows.overlay';
import { agRequestToQuery } from './ag-request-to-query.function';
import { IQueryFilter, QueryBuilder, QueryFilter } from '../../query/query.builder';

@Component({
  selector: 'soft-grid',
  imports: [
    AgGridAngular
  ],
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridComponent<T extends object> {
  private gridApi: GridApi | null = null;
  private languageService = inject(LanguageService);

  config = input.required<Grid<T>>();
  serverSideFilter = input<Dictionary<QueryFilter>>({});
  f = formatter();

  dataTypeDefinitions: {[p: string]: DataTypeDefinition} = {
    currency: {
      extendsDataType: 'number',
      baseDataType: 'number',
      valueFormatter: params => !isDefinedNotEmpty(params?.value)
        ? ''
        : '' + this.f(params.value, 'CURRENCY.DEFAULT', {currency: 'EUR'})(),
    },
    percent: {
      extendsDataType: 'number',
      baseDataType: 'number',
      valueFormatter: params => !isDefinedNotEmpty(params?.value)
        ? ''
        : '' + this.f(params.value, 'NUMBER.DECIMAL')() + '%',
    },
  };

  aggFuncs = {
    'single': params => {
      const values: any[] = params.values;
      const distinctValues = distinct(values, {comparer: equals})
      return distinctValues.length === 1 ? distinctValues[0] : null;
    }
  };

  gridOptions: Signal<GridOptions> = computed(() => {
    const config = this.config();
    let localeText = AG_GRID_LOCALE_DE;
    switch (this.languageService.selected().code) {
      case 'de':
        localeText = AG_GRID_LOCALE_DE;
        break;
      case 'en':
        localeText = AG_GRID_LOCALE_EN;
        break;
    }
    const rowModelType: RowModelType = isSSRMDataSource(config.dataSource) ? 'serverSide' : 'clientSide';

    const isServerSide = isSSRMDataSource(config.dataSource);
    return {
      sideBar: {
        toolPanels: ['columns', 'filters'],
        defaultToolPanel: 'columns'
      },
      localeText,
      rowModelType,
      rowGroupPanelShow: "always",
      autoSizeStrategy: {type: "fitCellContents"},
      enableCharts: true,
      cellSelection: true,
      dataTypeDefinitions: this.dataTypeDefinitions,
      loadingOverlayComponent: GridLoadingOverlay,
      loadingOverlayComponentParams: isResource(config.dataSource) ? {dataSource: config.dataSource} : undefined,
      noRowsOverlayComponent: GridNoRowsOverlay,
      noRowsOverlayComponentParams: isResource(config.dataSource) ? {dataSource: config.dataSource} : undefined,
      suppressServerSideFullWidthLoadingRow: isServerSide ? true : undefined,
      cacheBlockSize: isServerSide ? 100 : undefined,
      maxBlocksInCache: isServerSide ? 5 : undefined,
      rowBuffer: isServerSide ? 0 : undefined,
      maxConcurrentDatasourceRequests: isServerSide ? 1 : undefined,
      blockLoadDebounceMillis: isServerSide ? 200 : undefined,
      aggFuncs: this.aggFuncs,
      getRowId: params => {
        const data = params.data;
        if(isDefinedNotEmpty(data?.id))
          return '' + data.id;
        return JSON.stringify(data);
      },
      debug: isDevMode(),
      suppressContextMenu: true,
    }
  });

  columnDefs = computed<(ColDef | ColGroupDef)[]>(() => {
    const config = this.config();
    const columns = config.columns();
    const colDefs = toAgGridColDefs(columns, this.f, (col) => this.loadFilters(col));
    console.log('------------AG Grid ColDefs', colDefs);
    return colDefs;
  })

  loading = computed(() => {
    const dataSource = this.config().dataSource;
    if(isResource(dataSource))
      return dataSource.isLoading();
    return false;
  })

  rowData = computed(() => {
    const dataSource = this.config().dataSource;
    if(isSSRMDataSource(dataSource))
      return undefined;
    if(isServerSideDataSource(dataSource))
      return undefined;
    if(isSignal(dataSource))
      return dataSource();
    if(isResource(dataSource))
      return dataSource.value() ?? [];
    if(Array.isArray(dataSource))
      return dataSource;
    return [];
  })

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    sortable: true,
    filter: true,
  };

  autoGroupColumnDef = computed<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 280,
      sortable: false,
      pinned: 'left'
    };
  });

  constructor() {
    effect(() => {
      const config = this.config();
      const dataSource = config.dataSource;
      const serverSideFilter = this.serverSideFilter();
      if(!this.gridApi || (!isSSRMDataSource(dataSource) && !isServerSideDataSource(dataSource)))
        return;
      this.gridApi.refreshServerSide({purge: true});
    })
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    params.api.closeToolPanel();

    if(isSSRMDataSource(this.config().dataSource)) {
      const datasource = this.createDataSource();
      params.api.setGridOption("serverSideDatasource", datasource);
    }
  }

  onFirstDataRendered(params: FirstDataRenderedEvent) {
    params.api.autoSizeAllColumns();
  }

  createDataSource(): IServerSideDatasource {
    return {
      getRows: async (params: IServerSideGetRowsParams<any>) => {
        console.log('------------AG Grid Request', params)
        const dataSource = this.config().dataSource;
        if (!isSSRMDataSource(dataSource)) {
          params.fail();
          return;
        }
        try {
          const query =  agRequestToQuery(this.config().columns(), params.api, params.request, this.serverSideFilter())
          const result = await dataSource.load(query);
          params.success({
            rowData: result.data,
            pivotResultFields: result.pivotFields
          });
        } catch (e) {
          params.fail();
        }
      }
    }
  }

  async loadFilters(col: ServerSideColumn): Promise<unknown[]> {
    const dataSource = this.config().dataSource;
    const externalFilters = this.serverSideFilter();
    if(!isSSRMDataSource(dataSource) && !isServerSideDataSource(dataSource))
      return [];

    const sortField = col.serverSideSortField ? `${col.serverSideField}.${col.serverSideSortField}` : col.serverSideField;
    const builder = new QueryBuilder(0, 1000)
      .group(col.serverSideField)
      .sort(sortField ?? col.serverSideField, 'asc')

    for (const [ name, filter ] of Object.entries(externalFilters)) {
      if(isDefinedNotEmpty(filter))
        builder.filter(name, filter);
    }

    const result = await dataSource.load(builder.build());
    return result.data.map(o => getValue(o, col.serverSideField));
  }
}
